import { useRef, useEffect, useState, useLayoutEffect } from "react";

import Hls from "hls.js";
import { BrowseGalleryRounded, DoneAllRounded, DoneRounded, OpenInNewRounded } from "@mui/icons-material";

import api from "@helpers/api";
import { useAuth } from "@contexts/AuthContext";

import "./styles.scss";

import { UserAvatar } from "@components/Community";
import { useCommunity } from "@contexts/CommunityContext";

import { Button } from "@fragments";
import Parser from "@helpers/parser";

// Local storage key for video progress
const VIDEO_PROGRESS_KEY = "video_progress";

// Helper functions for video progress management
const getVideoProgress = () => {
    try {
        const stored = localStorage.getItem(VIDEO_PROGRESS_KEY);
        const result = stored ? JSON.parse(stored) : {};
        console.log('Retrieved video progress from localStorage:', { stored, parsed: result });
        return result;
    } catch (error) {
        console.error('Error reading video progress from localStorage:', error);
        return {};
    }
};

const saveVideoProgress = (videoId, positionMillis) => {
    try {
        const progress = getVideoProgress();
        progress[videoId] = positionMillis;
        localStorage.setItem(VIDEO_PROGRESS_KEY, JSON.stringify(progress));
        console.log('Video progress saved to localStorage:', { videoId, positionMillis, allProgress: progress });
    } catch (error) {
        console.error('Error saving video progress to localStorage:', error);
    }
};

const getStartPosition = (videoId, duration) => {
    const progress = getVideoProgress();
    const storedPosition = progress[videoId];

    if (!storedPosition || duration === 0) {
        return 0;
    }

    const storedSeconds = storedPosition / 1000;
    const durationSeconds = duration;

    // If stored position is less than 20 seconds from beginning, start from zero
    if (storedSeconds < 20) {
        return 0;
    }

    // If stored position is less than 40 seconds from end, start from zero
    if (storedSeconds > (durationSeconds - 40)) {
        return 0;
    }

    // Otherwise, start from stored position
    return storedSeconds;
};

function CanvasBlurEffect({ videoRef }) {
    const canvasA = useRef(null);
    const canvasB = useRef(null);

    // usamos um ref para evitar loops de re-render
    const showARef = useRef(true);

    useLayoutEffect(() => {
        const video = videoRef?.current;
        if (!video) return;

        const cA = canvasA.current;
        const cB = canvasB.current;
        if (!cA || !cB) return;

        const ctxA = cA.getContext("2d");
        const ctxB = cB.getContext("2d");
        if (!ctxA || !ctxB) return;

        function drawFrameToCanvas(canvas, ctx) {
            if (video.readyState < 2) return;
            try {
                ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            } catch (err) {
                console.warn("Erro ao renderizar frame:", err);
            }
        }

        function updateFrame() {
            const showingCanvas = showARef.current ? cA : cB;
            const hiddenCanvas = showARef.current ? cB : cA;
            const hiddenCtx = showARef.current ? ctxB : ctxA;

            // desenha frame no canvas oculto
            drawFrameToCanvas(hiddenCanvas, hiddenCtx);

            // animação crossfade
            hiddenCanvas.style.opacity = 0.22;
            showingCanvas.style.opacity = 0;

            // alterna o canvas sem causar re-render
            showARef.current = !showARef.current;
        }

        // primeiro draw
        updateFrame();

        // atualiza a cada 2 segundos
        const interval = setInterval(updateFrame, 6000);

        return () => clearInterval(interval);
    }, [videoRef]); // apenas quando o vídeo mudar

    const canvasStyle = {
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        objectFit: "cover",
        filter: "blur(45px)",
        transition: "opacity 3500ms ease",
    };

    return (
        <>
            <canvas className="canvas-blur-effect" ref={canvasA} style={{ ...canvasStyle, opacity: 1 }} />
            <canvas className="canvas-blur-effect" ref={canvasB} style={{ ...canvasStyle, opacity: 0 }} />
        </>
    );
}

// Badge sutil para administradores visualizando conteúdo pré-lançamento
function AdminPreviewBadge({ launchDate }) {
    return (
        <div className='admin-preview-badge'>
            <BrowseGalleryRounded fontSize='small' />
            <span>Vai ao ar em {Parser.date.readable(launchDate)}</span>
        </div>
    )
}

// Banner para conteúdo agendado (usuário não tem acesso mas vai ser lançado)
function LaunchDateBanner({ content }) {
    // Use availability.available_at if launch is null
    const displayDate = content.timestamps?.launch || content.availability?.available_at;

    return (
        <>
            <img src={content.banner} alt={content.name} className="content-banner" />
            <div className="launch-date-card">
                <BrowseGalleryRounded fontSize='medium' />
                <span>Vai ao ar no dia {Parser.date.readable(displayDate)}</span>
            </div>
        </>
    )
}

// Helper para verificar se uma data é futura
function isFutureDate(dateString) {
    if (!dateString) return false;
    const date = new Date(dateString);
    const now = new Date();
    return date > now;
}





export default function Player({ info, mode = "horizontal", containerRef }) {
    const videoRef = useRef(null);
    const hlsRef = useRef(null);
    const inactivityTimerRef = useRef(null);
    const viewUpdateIntervalRef = useRef(null);
    const lastViewUpdateRef = useRef(0);
    const progressSaveIntervalRef = useRef(null);

    const [watched, setWatched] = useState(info.watched);
    const [isRestMode, setIsRestMode] = useState(false);
    const [hasReached90Percent, setHasReached90Percent] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);

    const { user } = useAuth();

    const videoSrc = info.video?.src

    const registerWatchedStatus = async (contentId, watchedStatus) => {
        setWatched(watchedStatus);

        try {
            const payload = {
                content: contentId,
                watched: !!watchedStatus
            };

            const response = await api.post(`content/interaction/watched`, payload);

            if (response.status === 200 || response.status === 201) {
                console.log('Status de visualização registrado com sucesso:', payload);
                return true;
            } else {
                console.error('Erro ao registrar status de visualização:', response);
                return false;
            }
        } catch (error) {
            console.error('Erro na requisição de visualização:', error);
            return false;
        }
    };

    // Função para registrar visualização do conteúdo na API
    const registerContentView = async (position, millisIncrease) => {
        try {
            const payload = {
                lesson: info.id,
                user: { id: user?.id || 0 },
                position: position,
                millis_increase: millisIncrease
            };

            const response = await api.post(`content/interaction/view`, payload);

            if (response.status === 200 || response.status === 201) {
                console.log('Visualização do conteúdo registrada com sucesso:', payload);
                return true;
            } else {
                console.error('Erro ao registrar visualização do conteúdo:', response);
                return false;
            }
        } catch (error) {
            console.error('Erro na requisição de visualização do conteúdo:', error);
            return false;
        }
    };

    // Função para marcar como assistido
    const markAsWatched = async () => {
        registerWatchedStatus(info.id, true);
    };

    // Função para marcar como não assistido
    const markAsNotWatched = async () => {
        registerWatchedStatus(info.id, false);
    };

    // Função para verificar progresso do vídeo
    const checkVideoProgress = () => {
        const video = videoRef.current;
        if (!video || video.duration === 0) return;

        const currentTime = video.currentTime;
        const duration = video.duration;
        const progress = (currentTime / duration) * 100;

        // Se atingiu 90% e ainda não foi marcado como assistido
        if (progress >= 90 && !hasReached90Percent && !watched) {
            setHasReached90Percent(true);
            markAsWatched();
        }
    };

    // Função para salvar progresso do vídeo
    const saveCurrentProgress = () => {
        const video = videoRef.current;
        if (!video) {
            console.log('saveCurrentProgress: No video element');
            return;
        }

        if (video.duration === 0 || isNaN(video.duration)) {
            console.log('saveCurrentProgress: Video duration not ready yet', { duration: video.duration, currentTime: video.currentTime });
            return;
        }

        const currentTimeMillis = Math.floor(video.currentTime * 1000);
        console.log('Saving video progress:', {
            videoId: info.id,
            position: currentTimeMillis,
            duration: video.duration,
            currentTime: video.currentTime
        });
        saveVideoProgress(info.id, currentTimeMillis);
    };

    // Função para iniciar o intervalo de atualização de visualização
    const startViewUpdateInterval = () => {
        if (viewUpdateIntervalRef.current) {
            clearInterval(viewUpdateIntervalRef.current);
        }

        // Only start interval if user is authenticated
        if (!user?.id) {
            console.log('Not starting view update interval - user not authenticated');
            return;
        }

        const video = videoRef.current;
        if (video) {
            // Initialize the last update reference to current position
            lastViewUpdateRef.current = Math.floor(video.currentTime * 1000);
        }

        viewUpdateIntervalRef.current = setInterval(() => {
            const video = videoRef.current;
            if (!video || video.paused || video.ended || !user?.id) return;

            const currentTimeMillis = Math.floor(video.currentTime * 1000);
            const lastUpdate = lastViewUpdateRef.current;
            const millisIncrease = currentTimeMillis - lastUpdate;

            if (millisIncrease > 0) {
                // Save progress to localStorage every 5 seconds
                saveCurrentProgress();

                // Send view update to API
                registerContentView(currentTimeMillis, millisIncrease);
                lastViewUpdateRef.current = currentTimeMillis;
            }
        }, 5000);
    };

    // Função para parar o intervalo de atualização de visualização
    const stopViewUpdateInterval = () => {
        if (viewUpdateIntervalRef.current) {
            clearInterval(viewUpdateIntervalRef.current);
            viewUpdateIntervalRef.current = null;
        }
    };

    // Função para iniciar o intervalo de salvamento de progresso
    const startProgressSaveInterval = () => {
        if (progressSaveIntervalRef.current) {
            clearInterval(progressSaveIntervalRef.current);
        }

        progressSaveIntervalRef.current = setInterval(() => {
            const video = videoRef.current;
            if (!video || video.paused || video.ended) return;

            saveCurrentProgress();
        }, 5000);
    };

    // Função para parar o intervalo de salvamento de progresso
    const stopProgressSaveInterval = () => {
        if (progressSaveIntervalRef.current) {
            clearInterval(progressSaveIntervalRef.current);
            progressSaveIntervalRef.current = null;
        }
    };

    // Função para resetar o timer de inatividade
    const resetInactivityTimer = () => {
        if (inactivityTimerRef.current) {
            clearTimeout(inactivityTimerRef.current);
        }

        setIsRestMode(false);

        inactivityTimerRef.current = setTimeout(() => {
            setIsRestMode(true);
        }, 3000);
    };

    // Função para lidar com interações do usuário
    const handleUserInteraction = () => {
        resetInactivityTimer();
    };

    // Função para lidar com play do vídeo
    const handlePlay = () => {
        setIsPlaying(true);
        handleUserInteraction();
        const video = videoRef.current;
        if (video) {
            lastViewUpdateRef.current = Math.floor(video.currentTime * 1000);
            startViewUpdateInterval();
            startProgressSaveInterval(); // Start saving progress on play
        }
    };

    // Função para lidar com pause do vídeo
    const handlePause = () => {
        setIsPlaying(false);
        saveCurrentProgress();
        stopViewUpdateInterval();
        stopProgressSaveInterval(); // Stop saving progress on pause
    };

    // Função para lidar com fim do vídeo
    const handleEnded = () => {
        setIsPlaying(false);
        saveCurrentProgress();
        stopViewUpdateInterval();
        stopProgressSaveInterval(); // Stop saving progress on ended
    };

    useEffect(() => {
        const video = videoRef.current;
        if (!video || !videoSrc) return;

        const hls = new Hls({
            maxBufferLength: 30, // segundos
            maxMaxBufferLength: 60,
            liveSyncDuration: 10,
            enableWorker: true, // já é true por padrão, mas garanta isso
            lowLatencyMode: true,
        });
        hlsRef.current = hls;
        hls.loadSource(videoSrc);
        hls.attachMedia(video);

        // Adicionar event listeners para interações do usuário
        const videoElement = video;
        const containerElement = containerRef?.current || videoElement.parentElement;

        // Eventos do vídeo
        videoElement.addEventListener('play', handlePlay);
        videoElement.addEventListener('pause', handlePause);
        videoElement.addEventListener('ended', handleEnded);
        videoElement.addEventListener('seeking', handleUserInteraction);
        videoElement.addEventListener('volumechange', handleUserInteraction);
        videoElement.addEventListener('click', handleUserInteraction);
        videoElement.addEventListener('mousemove', handleUserInteraction);
        videoElement.addEventListener('mouseenter', handleUserInteraction);

        // Eventos do container
        if (containerElement) {
            containerElement.addEventListener('mouseenter', handleUserInteraction);
            containerElement.addEventListener('mousemove', handleUserInteraction);
            containerElement.addEventListener('click', handleUserInteraction);
        }

        // Iniciar timer de inatividade
        resetInactivityTimer();

        return () => {
            if (hlsRef.current) {
                hlsRef.current.destroy();
                hlsRef.current = null;
            }

            if (inactivityTimerRef.current) {
                clearTimeout(inactivityTimerRef.current);
            }

            if (viewUpdateIntervalRef.current) {
                clearInterval(viewUpdateIntervalRef.current);
            }

            if (progressSaveIntervalRef.current) {
                clearInterval(progressSaveIntervalRef.current);
            }

            // Remover event listeners
            videoElement.removeEventListener('play', handlePlay);
            videoElement.removeEventListener('pause', handlePause);
            videoElement.removeEventListener('ended', handleEnded);
            videoElement.removeEventListener('seeking', handleUserInteraction);
            videoElement.removeEventListener('volumechange', handleUserInteraction);
            videoElement.removeEventListener('click', handleUserInteraction);
            videoElement.removeEventListener('mousemove', handleUserInteraction);
            videoElement.removeEventListener('mouseenter', handleUserInteraction);

            if (containerElement) {
                containerElement.removeEventListener('mouseenter', handleUserInteraction);
                containerElement.removeEventListener('mousemove', handleUserInteraction);
                containerElement.removeEventListener('click', handleUserInteraction);
            }
        };
    }, [videoSrc, containerRef]);

    useEffect(() => {
        const video = videoRef.current;
        if (video) {
            // Get stored position and set video time
            const startPosition = getStartPosition(info.id, video.duration);

            if (startPosition > 0) {
                video.currentTime = startPosition;
            }

            video.play().catch(() => { });
        }
    }, [videoSrc, info.id]);

    // Monitorar progresso do vídeo
    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        const handleTimeUpdate = () => {
            checkVideoProgress();
        };

        video.addEventListener('timeupdate', handleTimeUpdate);

        return () => {
            video.removeEventListener('timeupdate', handleTimeUpdate);
        };
    }, [watched, hasReached90Percent]);

    // Save progress periodically and on pause/stop
    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        const handleBeforeUnload = () => {
            saveCurrentProgress();
            stopViewUpdateInterval();
            stopProgressSaveInterval();
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            stopViewUpdateInterval();
            stopProgressSaveInterval();
        };
    }, [info.id]);

    // Cleanup view update interval when component unmounts or video source changes
    useEffect(() => {
        return () => {
            stopViewUpdateInterval();
            stopProgressSaveInterval();
        };
    }, [videoSrc]);

    function ToggleWatchedButton() {
        return (
            <button
                className={`toggle-watched-button ${watched ? "watched" : ""} ${isRestMode ? "rest-mode" : ""}`}
                onClick={() => {
                    if (watched) {
                        markAsNotWatched();
                    } else {
                        markAsWatched();
                    }
                }}
            >
                {
                    watched
                        ? <DoneAllRounded className="toggle-watched-icon" />
                        : <DoneRounded className="toggle-watched-icon" />
                }
                <span>
                    {
                        watched ? "Assistido" : "Marcar como assistido"
                    }
                </span>
            </button>
        )
    }

    // Helper function to check if user has special permissions (admin, owner, or creator)
    const hasSpecialPermissions = () => {
        if (!info?.role) return false;
        // Check if user role is admin, owner, or creator
        return ['admin', 'owner', 'creator'].includes(info.role);
    };

    return (<>
        <div
            className={`player center ${mode} ${isRestMode ? "rest-mode" : ""}`}
            ref={containerRef}
            onMouseEnter={handleUserInteraction}
            onMouseMove={handleUserInteraction}
            onClick={handleUserInteraction}
        >
            {/* Lógica de renderização baseada em disponibilidade */}
            {(() => {
                // Check both timestamps.launch and availability.available_at for future dates
                const launchDate = info?.timestamps?.launch;
                const availableAtDate = info?.availability?.available_at;
                const futureDate = launchDate || availableAtDate;
                const isFuture = isFutureDate(futureDate);
                const isAvailable = info?.availability?.available;
                const hasPermissions = hasSpecialPermissions();

                // RULE 1: Video has a future date (launch or available_at)
                if (isFuture) {
                    // User has admin/owner/creator permissions - show video with overlay
                    if (hasPermissions) {
                        return (
                            <video
                                ref={videoRef}
                                controls
                                playsInline
                                preload="auto"
                                style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                    backgroundColor: "black",
                                    borderRadius: "inherit",
                                    position: "relative",
                                    zIndex: 1,
                                }}
                            />
                        );
                    }
                    // User does not have permissions - show only launch date banner
                    return <LaunchDateBanner content={info} />;
                }

                // RULE 2: Video requires subscription and user is not owner/admin/creator
                if (!isAvailable && !hasPermissions && info?.id) {
                    return (
                        <PrivateVideoBanner
                            content={info}
                            community={info?.community}
                        />
                    );
                }

                // RULE 3: Video is available - play it
                return (
                    <video
                        ref={videoRef}
                        controls
                        playsInline
                        preload="auto"
                        style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            backgroundColor: "black",
                            borderRadius: "inherit",
                            position: "relative",
                            zIndex: 1,
                        }}
                    />
                );
            })()}




            {/* Container flex para elementos do topo */}
            <div className="player-top-bar">
                <UserAvatar
                    user={info?.creator}
                    size={26}
                    displayName={true}
                    className="player-creator-avatar"
                />

                <div className="player-top-right">
                    {hasSpecialPermissions() && isFutureDate(info?.timestamps?.launch || info?.availability?.available_at) && (
                        <AdminPreviewBadge launchDate={info?.timestamps?.launch || info?.availability?.available_at} />
                    )}
                    <ToggleWatchedButton />
                </div>
            </div>
        </div></>

    );
};


function PrivateVideoBanner({ community, content }) {
    const { community: currentCommunity } = useCommunity()

    const currentCommunityInfo = currentCommunity?.get(community)

    const { user } = useAuth()

    if (!currentCommunityInfo || !content) return null

    const planWhichIsAvailable = currentCommunityInfo?.plans?.find(p => p.id == content.availability?.plans?.[0])


    let checkoutLink = `https://pay.ginastee.com/${currentCommunityInfo?.checkout}?email=${user.email.address}&name=${user.username}`

    if (planWhichIsAvailable) {
        checkoutLink += `&plan=${planWhichIsAvailable?.id}`
    }

    if (user.phone?.number) {
        checkoutLink += `&phone=${user.phone.number?.replace('+55', '')}`
    }

    if (user.document) {
        checkoutLink += `&document=${user.document}`
    }


    return (
        <>
            <img src={content.banner} alt={content.name} className="content-banner" />

            <div className="request-upgrade-banner px-4 py-4">
                <small className="premium-message-text">Desbloqueie este recurso premium assinando a comunidade</small>
                <img src={currentCommunityInfo?.banner} alt={currentCommunityInfo?.name} className="community-banner mt-2" />
                <h4 className="mb-1">{currentCommunityInfo?.name}</h4>
                <Button
                    text="Ver planos"
                    color="white"
                    tooltip={"Ver planos da comunidade " + currentCommunityInfo?.name}
                    startIcon={<OpenInNewRounded />}
                    onClick={() => window.location.href = checkoutLink}
                />
            </div >
        </>
    )
}